import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLink, 
  faCopy, 
  faExternalLinkAlt,
  faSpinner,
  faSync,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { keyUserActiveEnterpriseId } from '../../../assets/js/SessionStorageNames';
import PopUp from '../../general/popUps/PopUp';
import { getOdooPublicTempUrl, getProviderLogs, generateBulkPublicTempUrls, cancelProviderLink } from '../../../hooks/api/odoo';

const CreateProvider = () => {
  const globalContext = useContext(GlobalContext);
  const [showPopUp, setShowPopUp] = useState(false);
  const [popUpData, setPopUpData] = useState({ message: "", action: "", status: "" });
  const [activeTab, setActiveTab] = useState('custom'); // 'logs' o 'custom'
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Estados para Registros Personalizado (CRUD)
  const [customLinks, setCustomLinks] = useState([]);
  const [customLinksLoading, setCustomLinksLoading] = useState(false);
  const [creatingLinks, setCreatingLinks] = useState(false);
  const [bulkCreateCount, setBulkCreateCount] = useState(1);
  const [customError, setCustomError] = useState(null);
  const [customSuccess, setCustomSuccess] = useState(null);
  const [customLinksPage, setCustomLinksPage] = useState(1);
  const [customLinksLimit, setCustomLinksLimit] = useState(10);
  const [customLinksTotal, setCustomLinksTotal] = useState(0);
  const [customLinksTotalPages, setCustomLinksTotalPages] = useState(1);
  const [expiresIn, setExpiresIn] = useState(3600); // Por defecto 1 hora (3600 segundos)
  
  // Referencias para cleanup de timeouts
  const isMountedRef = useRef(true);
  const timeoutRefs = useRef([]);
  
  // Función auxiliar para crear timeout con cleanup
  const createTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        callback();
      }
    }, delay);
    timeoutRefs.current.push(timeoutId);
    return timeoutId;
  }, []);
  
  // Limpiar todos los timeouts al desmontar
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current = [];
    };
  }, []);

  // Función para cargar los logs
  const loadLogs = useCallback(async (page = 1, limit = 10) => {
    setLogsLoading(true);
    try {
      const entId = localStorage.getItem(keyUserActiveEnterpriseId) || globalContext.currentEnterpriseId;
      if (!entId) {
        throw new Error('No se pudo obtener el ID de la empresa');
      }

      const response = await getProviderLogs(entId, page, limit);
      
      if (response) {
        const logsData = response.logs || response.data || response.registers || [];
        const pagination = response.pagination || {};
        const total = pagination.total || response.total || response.totalLogs || response.totalRegisters || 0;
        const totalPagesCount = pagination.totalPages || response.totalPages || Math.ceil(total / limit);
        const currentPageNum = pagination.page || page;
        
        setLogs(logsData);
        setTotalLogs(total);
        setTotalPages(totalPagesCount);
        setCurrentPage(currentPageNum);
      }
    } catch (error) {
      console.error('Error al cargar logs:', error);
      setLogs([]);
      setPopUpData({
        message: error.response?.data?.message || error.message || 'Error al cargar los logs.',
        action: 'Error',
        status: 'error'
      });
      setShowPopUp(true);
    } finally {
      setLogsLoading(false);
    }
  }, [globalContext.currentEnterpriseId]);

  // Cargar logs cuando se cambia al tab de logs
  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs(1, pageSize);
    }
  }, [activeTab, loadLogs, pageSize]);

  // Funciones CRUD para Registros Personalizado
  const loadCustomLinks = useCallback(async (page = customLinksPage, limit = customLinksLimit) => {
    setCustomLinksLoading(true);
    setCustomError(null);
    try {
      const entId = localStorage.getItem(keyUserActiveEnterpriseId) || globalContext.currentEnterpriseId;
      if (!entId) {
        throw new Error('No se pudo obtener el ID de la empresa');
      }

      const response = await getOdooPublicTempUrl(entId, page, limit);

      let tokensArray = [];
      if (response.tokens && Array.isArray(response.tokens)) {
        tokensArray = response.tokens;
      } else if (response.links && Array.isArray(response.links)) {
        tokensArray = response.links;
      } else if (Array.isArray(response)) {
        tokensArray = response;
      } else if (response.data && Array.isArray(response.data)) {
        tokensArray = response.data;
      }

      const pagination = response.pagination || {};
      const total = pagination.total || response.total || tokensArray.length;
      const totalPagesCount = pagination.totalPages || response.totalPages || Math.ceil(total / limit);
      const currentPageNum = pagination.page || page;

      const formattedLinks = tokensArray.map((tokenItem, index) => {
        const baseUrl = window.location.origin;
        
        let token = tokenItem.token;
        let fullUrl = tokenItem.url;
        // Validar que token exista antes de usarlo en la URL
        if (!fullUrl && token) {
          fullUrl = `${baseUrl}/provider-client/${entId}?token=${token}`;
        } else if (!fullUrl && !token) {
          fullUrl = `${baseUrl}/provider-client/${entId}`;
        } else if (fullUrl && !fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
          fullUrl = fullUrl.startsWith('/') ? `${baseUrl}${fullUrl}` : `${baseUrl}/${fullUrl}`;
        }

        return {
          name: tokenItem.name || `Link ${((page - 1) * limit) + index + 1}`,
          url: fullUrl,
          createdAt: tokenItem.createdAt || tokenItem.created_at || new Date().toISOString(),
          token: token,
          available: tokenItem.available !== undefined ? tokenItem.available : true,
          tokenExpired: tokenItem.tokenExpired || false,
          message: tokenItem.message || ''
        };
      });

      setCustomLinks(formattedLinks);
      setCustomLinksTotal(total);
      setCustomLinksTotalPages(totalPagesCount);
      setCustomLinksPage(currentPageNum);
      setCustomLinksLimit(limit);
    } catch (error) {
      console.error('Error al cargar links personalizados:', error);
      setCustomError(error.response?.data?.message || error.message || 'Error al cargar los links personalizados');
      setCustomLinks([]);
    } finally {
      setCustomLinksLoading(false);
    }
  }, [globalContext.currentEnterpriseId, customLinksPage, customLinksLimit]);

  const createBulkLinks = async () => {
    if (bulkCreateCount < 1 || bulkCreateCount > 20) {
      setCustomError('Debes crear entre 1 y 20 links a la vez');
      createTimeout(() => setCustomError(null), 5000);
      return;
    }

    if (expiresIn < 60 || expiresIn > 604800) {
      setCustomError('El tiempo de duración debe estar entre 1 minuto y 7 días');
      createTimeout(() => setCustomError(null), 5000);
      return;
    }

    setCreatingLinks(true);
    setCustomError(null);
    setCustomSuccess(null);

    try {
      const entId = localStorage.getItem(keyUserActiveEnterpriseId) || globalContext.currentEnterpriseId;
      if (!entId) {
        throw new Error('No se pudo obtener el ID de la empresa');
      }

      const response = await generateBulkPublicTempUrls(entId, bulkCreateCount, expiresIn);

      let linksArray = [];
      if (response.links && Array.isArray(response.links)) {
        linksArray = response.links;
      } else if (Array.isArray(response)) {
        linksArray = response;
      } else if (response.urls && Array.isArray(response.urls)) {
        linksArray = response.urls;
      } else if (response.data && Array.isArray(response.data)) {
        linksArray = response.data;
      } else if (response.url) {
        linksArray = [{ url: response.url, token: response.token || '' }];
      }

      if (linksArray.length === 0) {
        throw new Error('No se recibieron links del servidor');
      }

      await loadCustomLinks();
      setCustomSuccess(`Se crearon ${linksArray.length} link(s) exitosamente`);
      createTimeout(() => setCustomSuccess(null), 5000);

      setBulkCreateCount(1);
    } catch (error) {
      console.error('Error al crear links masivos:', error);
      setCustomError(error.response?.data?.message || error.message || 'Error al crear los links');
      createTimeout(() => setCustomError(null), 5000);
    } finally {
      setCreatingLinks(false);
    }
  };

  const deleteCustomLink = async (token) => {
    if (window.confirm('¿Estás seguro de que deseas cancelar este link?')) {
      try {
        const entId = localStorage.getItem(keyUserActiveEnterpriseId) || globalContext.currentEnterpriseId;
        if (!entId) {
          throw new Error('No se pudo obtener el ID de la empresa');
        }

        // El id es el registerId (el _id del token)
        await cancelProviderLink(entId, token);
        
        // Recargar los links desde el backend después de cancelar
        await loadCustomLinks(customLinksPage, customLinksLimit);
        setCustomSuccess('Link cancelado exitosamente');
        createTimeout(() => setCustomSuccess(null), 5000);
      } catch (error) {
        console.error('Error al cancelar link:', error);
        setCustomError(error.response?.data?.message || error.message || 'Error al cancelar el link');
        createTimeout(() => setCustomError(null), 5000);
      }
    }
  };

  const copyCustomLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCustomSuccess('Link copiado al portapapeles');
      createTimeout(() => setCustomSuccess(null), 3000);
    }).catch((err) => {
      console.error('Error al copiar:', err);
      setCustomError('Error al copiar el link');
      createTimeout(() => setCustomError(null), 5000);
    });
  };

  // Cargar links personalizados cuando se cambia al tab
  useEffect(() => {
    if (activeTab === 'custom') {
      loadCustomLinks();
    }
  }, [activeTab, loadCustomLinks]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
      padding: '2rem 0'
    }}>
      {showPopUp && (
        <PopUp 
          setShow={setShowPopUp} 
          message={popUpData.message} 
          action={popUpData.action} 
          status={popUpData.status} 
        />
      )}

      <div className="container">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12 text-center">
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '7px',
              padding: '1.4rem 2rem',
              color: 'white',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <h1 style={{ 
                fontSize: '1.6rem', 
                fontWeight: 'bold', 
                marginBottom: '0.5rem' 
              }}>
                Generar Link de Registro
              </h1>
              <p style={{
                fontSize: '.9rem', 
                opacity: 0.9,
                margin: 0 
              }}>
                Crea y comparte links para que los proveedores se registren en el sistema
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="row mb-4">
          <div className="col-lg-8 mx-auto">
            <div className="card shadow-sm border-0" style={{ borderRadius: '16px' }}>
              <div className="card-body p-0">
                <ul className="nav nav-tabs border-0" style={{ 
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                  backgroundColor: '#f8f9fa',
                  display: 'flex'
                }}>
                  <li className="nav-item" style={{ flex: 1 }}>
                    <button
                      className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
                      onClick={() => setActiveTab('logs')}
                      style={{
                        border: 'none',
                        borderRadius: activeTab === 'logs' ? '16px 16px 0 0' : '0',
                        backgroundColor: activeTab === 'logs' ? 'white' : 'transparent',
                        color: activeTab === 'logs' ? '#3b82f6' : '#6b7280',
                        fontWeight: activeTab === 'logs' ? '600' : '500',
                        padding: '1rem 2rem',
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Registros
                    </button>
                  </li>
                  <li className="nav-item" style={{ flex: 1 }}>
                    <button
                      className={`nav-link ${activeTab === 'custom' ? 'active' : ''}`}
                      onClick={() => setActiveTab('custom')}
                      style={{
                        border: 'none',
                        borderRadius: activeTab === 'custom' ? '16px 16px 0 0' : '0',
                        backgroundColor: activeTab === 'custom' ? 'white' : 'transparent',
                        color: activeTab === 'custom' ? '#3b82f6' : '#6b7280',
                        fontWeight: activeTab === 'custom' ? '600' : '500',
                        padding: '1rem 2rem',
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Links de registros
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido de los Tabs */}
        {activeTab === 'logs' && (
          /* Tab de Logs */
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#1e40af' }}>
                      Logs de Registro
                    </h5>
                    <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
                      <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                        <label className="mb-0" style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                          Mostrar:
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={pageSize}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value);
                            const newPageSize = Number.isNaN(parsed) ? 10 : parsed;
                            setPageSize(newPageSize);
                            setCurrentPage(1);
                            loadLogs(1, newPageSize);
                          }}
                          disabled={logsLoading}
                          style={{
                            width: '80px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            padding: '0.4rem 0.6rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => loadLogs(currentPage, pageSize)}
                        disabled={logsLoading}
                        style={{
                          borderRadius: '8px',
                          padding: '0.5rem 1rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faSync} spin={logsLoading} className="me-2" />
                        Actualizar
                      </button>
                    </div>
                  </div>

                  {logsLoading ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#3b82f6' }} />
                      <p className="mt-3 text-muted">Cargando logs...</p>
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No hay logs disponibles</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                          <tr>
                            <th style={{ fontWeight: '600', color: '#374151' }}>Fecha</th>
                            <th style={{ fontWeight: '600', color: '#374151' }}>Datos del proveedor</th>
                            <th style={{ fontWeight: '600', color: '#374151' }}>Estado</th>
                            <th style={{ fontWeight: '600', color: '#374151' }}>Fecha de actualización</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log, index) => {
                            const isCompleted = log.completed === true;
                            
                            // Mostrar solo la fecha de actualización
                            const detailsText = log.updatedAt 
                              ? `${new Date(log.updatedAt).toLocaleString('es-ES')}`
                              : 'Sin fecha de actualización';
                            
                            return (
                              <tr key={log._id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ color: '#6b7280' }}>
                                  {log.createdAt || log.date 
                                    ? new Date(log.createdAt || log.date).toLocaleString('es-ES')
                                    : 'N/A'}
                                </td>
                                <td style={{ color: '#374151', fontWeight: '500' }}>
                                  {isCompleted ? (log.name || 'Registro de Proveedor') : 'Registro de Proveedor'}
                                </td>
                                <td>
                                  {isCompleted ? (
                                    <span 
                                      className="badge"
                                      style={{
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        padding: '0.4rem 0.8rem',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Completado
                                    </span>
                                  ) : (
                                    <span 
                                      className="badge"
                                      style={{
                                        backgroundColor: '#f59e0b',
                                        color: 'white',
                                        padding: '0.4rem 0.8rem',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Token Expirado
                                    </span>
                                  )}
                                </td>
                                <td style={{ color: '#6b7280', maxWidth: '400px' }}>
                                  <div style={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.85rem'
                                  }} title={detailsText}>
                                    {detailsText}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Paginación - Siempre visible */}
                  {!logsLoading && (
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                      <div>
                        <small className="text-muted">
                          Mostrando {totalLogs > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} - {Math.min(currentPage * pageSize, totalLogs)} de {totalLogs} logs
                        </small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {currentPage > 1 && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => loadLogs(currentPage - 1, pageSize)}
                            disabled={logsLoading || totalPages === 0}
                            style={{
                              borderRadius: '8px',
                              padding: '0.5rem 1rem',
                              minWidth: '90px'
                            }}
                          >
                            Anterior
                          </button>
                        )}
                        {totalPages > 0 && (
                          <span className="d-flex align-items-center px-3" style={{ color: '#374151', fontWeight: '500' }}>
                            Página {currentPage} de {totalPages}
                          </span>
                        )}
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => loadLogs(currentPage + 1, pageSize)}
                          disabled={currentPage >= totalPages || logsLoading || totalPages === 0}
                          style={{
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            minWidth: '90px'
                          }}
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'custom' && (
          /* Tab de Registros Personalizado */
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                  {/* Mensajes de éxito y error */}
                  {customError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ borderRadius: '12px' }}>
                      <strong>Error:</strong> El link ya venció. Por favor, contacta con el administrador para obtener un nuevo link de registro.
                      <button type="button" className="btn-close" onClick={() => setCustomError(null)} aria-label="Close"></button>
                    </div>
                  )}
                  {customSuccess && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ borderRadius: '12px' }}>
                      <strong>Éxito:</strong> {customSuccess}
                      <button type="button" className="btn-close" onClick={() => setCustomSuccess(null)} aria-label="Close"></button>
                    </div>
                  )}

                  {/* Sección de creación masiva */}
                  <div className="mb-4" style={{ 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '16px',
                    padding: '2rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(59, 130, 246, 0.1)'
                  }}>
                    <div className="d-flex align-items-center mb-4">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '1rem',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}>
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: '1.25rem', color: 'white' }} />
                      </div>
                      <div>
                        <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '1.25rem' }}>
                          Crear Links Masivos
                        </h5>
                        <p className="mb-0" style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          Genera múltiples links de registro con duración personalizada
                        </p>
                      </div>
                    </div>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>
                          Cantidad de Links
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          max="20"
                          value={bulkCreateCount}
                          onChange={(e) => setBulkCreateCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                          style={{ 
                            borderRadius: '10px',
                            border: '2px solid #e5e7eb',
                            padding: '0.75rem 1rem',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: 'white',
                            height: '48px'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <small className="text-muted absolute" style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', position: 'absolute' }}>
                          Mín: 1, Máx: 20
                        </small>
                      </div>
                      <div className="col-md-4 d-flex flex-column justify-content-end">
                        <label className="form-label fw-bold mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>
                          Duración del Link
                        </label>
                        <select
                          className="form-select"
                          value={expiresIn}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value);
                            setExpiresIn(Number.isNaN(parsed) ? 3600 : parsed);
                          }}
                          style={{ 
                            borderRadius: '10px',
                            border: '2px solid #e5e7eb',
                            padding: '0.75rem 1rem',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            height: '48px'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value={3600}>1 Hora</option>
                          <option value={7200}>2 Horas</option>
                          <option value={10800}>3 Horas</option>
                          <option value={14400}>4 Horas</option>
                          <option value={18000}>5 Horas</option>
                          <option value={21600}>6 Horas</option>
                          <option value={28800}>8 Horas</option>
                          <option value={43200}>12 Horas</option>
                          <option value={86400}>1 Día</option>
                          <option value={172800}>2 Días</option>
                          <option value={259200}>3 Días</option>
                          <option value={345600}>4 Días</option>
                          <option value={432000}>5 Días</option>
                          <option value={518400}>6 Días</option>
                          <option value={604800}>7 Días</option>
                        </select>
                      </div>
                      <div className="col-md-5 d-flex flex-column justify-content-end">
                        <label className="form-label fw-bold mb-2" style={{ color: '#374151', fontSize: '0.9rem', visibility: 'hidden' }}>
                          Botón
                        </label>
                        <button
                          className="btn btn-primary w-100"
                          onClick={createBulkLinks}
                          disabled={creatingLinks}
                          style={{
                            borderRadius: '10px',
                            padding: '0.875rem 1.5rem',
                            fontWeight: '600',
                            background: creatingLinks 
                              ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            border: 'none',
                            fontSize: '0.95rem',
                            boxShadow: creatingLinks 
                              ? 'none'
                              : '0 4px 12px rgba(59, 130, 246, 0.3)',
                            transition: 'all 0.3s ease',
                            height: '48px'
                          }}
                          onMouseEnter={(e) => {
                            if (!creatingLinks) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!creatingLinks) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                            }
                          }}
                        >
                          {creatingLinks ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Creando...
                            </>
                          ) : (
                            <>
                              Crear {bulkCreateCount} Link{bulkCreateCount !== 1 ? 's' : ''}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Lista de links personalizados */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#1e40af' }}>
                        Links Personalizados ({customLinksTotal})
                      </h5>
                      <div className="d-flex align-items-center" style={{gap: '10px'}}>
                        <div className="d-flex align-items-center" style={{gap: '10px'}}>
                          <label className="mb-0" style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                            Mostrar:
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={customLinksLimit}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value);
                              const newLimit = Number.isNaN(parsed) ? 10 : parsed;
                              setCustomLinksLimit(newLimit);
                              setCustomLinksPage(1);
                              loadCustomLinks(1, newLimit);
                            }}
                            disabled={customLinksLoading}
                            style={{
                              width: '80px',
                              borderRadius: '8px',
                              border: '1px solid #d1d5db',
                              padding: '0.4rem 0.6rem',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => loadCustomLinks(customLinksPage, customLinksLimit)}
                          disabled={customLinksLoading}
                          style={{ borderRadius: '8px' }}
                        >
                          <FontAwesomeIcon icon={faSync} spin={customLinksLoading} className="me-2" />
                          Actualizar
                        </button>
                      </div>
                    </div>

                    {customLinksLoading ? (
                      <div className="text-center py-5">
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#3b82f6' }} />
                        <p className="mt-3 text-muted">Cargando links...</p>
                      </div>
                    ) : customLinks.length === 0 ? (
                      <div className="text-center py-5" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                        <FontAwesomeIcon icon={faLink} style={{ fontSize: '3rem', color: '#9ca3af', marginBottom: '1rem' }} />
                        <p className="text-muted mb-0">No hay links personalizados. Crea uno para comenzar.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th style={{ fontWeight: '600', color: '#374151' }}>#</th>
                              <th style={{ fontWeight: '600', color: '#374151' }}>Nombre</th>
                              <th style={{ fontWeight: '600', color: '#374151' }}>URL</th>
                              <th style={{ fontWeight: '600', color: '#374151' }}>Fecha de Creación</th>
                              <th style={{ fontWeight: '600', color: '#374151', textAlign: 'center' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customLinks.map((link, index) => (
                              <tr key={link.token || `link-${index}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ color: '#6b7280', fontWeight: '500' }}>{index + 1}</td>
                                <td style={{ color: '#374151', fontWeight: '500' }}>{link.name}</td>
                                <td style={{ color: '#6b7280', maxWidth: '400px' }}>
                                  <div style={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {link.url}
                                  </div>
                                </td>
                                <td style={{ color: '#6b7280' }}>
                                  {link.createdAt 
                                    ? new Date(link.createdAt).toLocaleString('es-ES')
                                    : 'N/A'}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div className="d-flex justify-content-center" style={{gap: '10px'}}>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => copyCustomLink(link.url)}
                                      style={{ borderRadius: '6px' }}
                                      title="Copiar link"
                                    >
                                      <FontAwesomeIcon icon={faCopy} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => window.open(link.url, '_blank')}
                                      style={{ borderRadius: '6px' }}
                                      title="Abrir link"
                                    >
                                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => deleteCustomLink(link.token)}
                                      style={{ borderRadius: '6px' }}
                                      title="Eliminar link"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Paginación */}
                    {!customLinksLoading && (
                      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                        <div>
                          <small className="text-muted">
                            Mostrando {customLinksTotal > 0 ? ((customLinksPage - 1) * customLinksLimit) + 1 : 0} - {Math.min(customLinksPage * customLinksLimit, customLinksTotal)} de {customLinksTotal} links
                          </small>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          {customLinksPage > 1 && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => loadCustomLinks(customLinksPage - 1, customLinksLimit)}
                              disabled={customLinksLoading}
                              style={{
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                minWidth: '90px'
                              }}
                            >
                              Anterior
                            </button>
                          )}
                          {customLinksTotalPages > 0 && (
                            <span className="d-flex align-items-center px-3" style={{ color: '#374151', fontWeight: '500' }}>
                              Página {customLinksPage} de {customLinksTotalPages}
                            </span>
                          )}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => loadCustomLinks(customLinksPage + 1, customLinksLimit)}
                            disabled={customLinksPage >= customLinksTotalPages || customLinksLoading}
                            style={{
                              borderRadius: '8px',
                              padding: '0.5rem 1rem',
                              minWidth: '90px'
                            }}
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProvider;

